import { useEffect, useState } from 'react';
import { PAUSE_POLLING_WHEN_UNFOCUSED } from 'src/constants/polling.constants';
import { useGetCreativeImportStatusQuery } from '../endpoints/creative-import.endpoints';
import {
  clearStoredCreativeImportRun,
  getStoredCreativeImportRun,
} from '../services/creative-import-run-storage';
import type { CreativeImportRunStatus } from '../types/creative-import.types';

const CREATIVE_IMPORT_POLL_INTERVAL_MS = 4_000;

const IN_PROGRESS_STATUSES: CreativeImportRunStatus[] = ['pending', 'running'];

export function isCreativeImportInProgress(status?: CreativeImportRunStatus | null): boolean {
  return status != null && IN_PROGRESS_STATUSES.includes(status);
}

export function useCreativeImportProgress(clientId: string | undefined) {
  const [runId, setRunId] = useState<string | null>(null);
  const [pollingInterval, setPollingInterval] = useState(0);

  useEffect(() => {
    if (!clientId) {
      setRunId(null);
      return;
    }
    const stored = getStoredCreativeImportRun(clientId);
    setRunId(stored?.runId ?? null);
  }, [clientId]);

  const { data, isError, isFetching } = useGetCreativeImportStatusQuery(
    { clientId: clientId ?? '', runId: runId ?? '' },
    {
      skip: !clientId || !runId,
      pollingInterval,
      ...PAUSE_POLLING_WHEN_UNFOCUSED,
    }
  );

  useEffect(() => {
    if (isError) {
      setPollingInterval(0);
      return;
    }
    setPollingInterval(
      isCreativeImportInProgress(data?.status) ? CREATIVE_IMPORT_POLL_INTERVAL_MS : 0
    );
  }, [data?.status, isError]);

  useEffect(() => {
    if (!clientId || !runId || isError) {
      return;
    }
    if (data?.status && !isCreativeImportInProgress(data.status)) {
      clearStoredCreativeImportRun(clientId);
    }
  }, [clientId, data?.status, isError, runId]);

  const beginTracking = (nextRunId: string) => {
    setRunId(nextRunId);
    setPollingInterval(CREATIVE_IMPORT_POLL_INTERVAL_MS);
  };

  const isInProgress = !isError && isCreativeImportInProgress(data?.status);

  return {
    runId,
    status: data,
    isInProgress,
    isFetching,
    beginTracking,
  };
}
